import { defineStore } from 'pinia'
import type {
  AddressFormConfig,
  BillingRegion,
  Country,
  CountryAPIData,
  PersonalInfoField,
  Region,
  StampinupDomain,
  SwitchLocalePayload
} from '~/types/locale'
import {
  CountryCode,
  Culture,
  Currency,
  LocaleSource,
  RegionCode,
  RegionAndCountryFields,
  Language
} from '~/types/locale'
import { stringifyQuery } from '~/utils'
import {
  getLanguagePath,
  getLocaleFromStampinupDomain,
  getStampinupDomainFromHost,
  getStampinupDomain,
  getStampinupDomainEnvPrefix,
  languagesForDomain
} from '~/utils/locale'
import { stringToEnumValue } from '~/utils/general'
import { regions as importedRegions } from '~/stores/_imports/regions'
import { eftRegions as importedEftRegions } from '~/stores/_imports/eftRegions'
import { billingRegions as importedBillingRegions } from '~/stores/_imports/billingRegions'
import { Env, Protocol } from '~/types/environment'
import { toError } from '~/utils/http/api-error'
import type { DemoContact } from '~/types/demo'

import { useCartStore } from '~/stores/cart'
import { useRedirect } from '~/composables/auth'
import { usePersistentStore } from '~/stores/persistent'
import { useRootStore } from '~/stores/root'
import type {ArrayOfTOneOrMore} from "~/types/utility"
import type {RouteLocationNormalizedLoaded} from "#vue-router";
import { useTypedRuntimeConfig } from '~/utils/runtimeConfig'
import { isAttributionKey, findAttributionParam } from '~/utils/seo'
import type { AttributionSelection } from '~/utils/seo'

export const useLocaleStore = defineStore('locale', () => {
  const { $appInsights } = useNuxtApp()
  const $config = useTypedRuntimeConfig()
  const route = useRoute()

  const localeSource = ref(LocaleSource.DefaultOrFailBack)
  // Static config, never written at runtime, so it stays out of state: as a ref it was
  // serialized into every page's payload (~11.5 KB). The only part that changes, the states
  // `getStatesForCountry` fetches, lives in `fetchedStates` instead. That is state, so readers
  // re-run when a list lands and the lists fetched during SSR reach the client in the payload.
  // `mergedRegions` lays them over the config once; every accessor below reads it, never
  // `regions` itself, so a country has its states whichever accessor returned it.
  const regions = importedRegions as ArrayOfTOneOrMore<Region>
  const fetchedStates = ref<Partial<Record<CountryCode, Pick<Country, 'stateFieldName' | 'states'>>>>({})
  const mergedRegions = computed(() => regions.map(region => ({
    ...region,
    countries: region.countries.map(country => ({ ...country, ...fetchedStates.value[country.countryCode] }))
  })) as ArrayOfTOneOrMore<Region>)
  const countries = computed(() => mergedRegions.value.map(region => region.countries).flat())
  const culture = ref(Culture.EN_US)
  const baseUrl = ref('')
  const billingRegions = ref<BillingRegion[]>([])
  const env = ref<Env>(Env.Release)
  const isLocaleSet = ref(false)
  const subLocaleLang = ref<string>()

  const region = (countryCode?: string) => {
    const regionList = mergedRegions.value
    return !countryCode
      ? regionList.find(region => region.countries.find(country => country.culture === culture.value)) ?? regionList[0]
      : regionList.find(region => region.countries.some(country => country.countryCode === countryCode)) ?? regionList.find(region => region.regionCode === RegionCode.EU) as Region
  }

  const country = (countryCode?: string) =>
    !countryCode
      ? region().countries.find(country => country.culture === culture.value) ?? region().countries[0]
      : countries.value.find(country => country.countryCode === countryCode) ?? {} as Country
  const sortedRegions = computed(() => {
    const regionList = JSON.parse(JSON.stringify(mergedRegions.value)) as Region[]
    regionList.sort((a, b) => a.name < b.name ? -1 : 1)
    regionList.forEach(r => r?.countries.sort((a: Country, b: Country) => new Intl.Collator().compare(a.name, b.name)))
    const currentRegion = regionList.find(r => r.name === region().name) as Region
    return [
      {
        ...currentRegion,
        countries: [country(), ...currentRegion?.countries?.filter(c => c.culture !== country().culture) ?? []]
      },
      ...regionList.filter(r => r.name !== region().name)
    ]
  }

  )
  const countryIsAt = computed(() => country().countryCode === CountryCode.AT)
  const countryIsAu = computed(() => country().countryCode === CountryCode.AU)
  const countryIsNz = computed(() => country().countryCode === CountryCode.NZ)
  const countryIsIe = computed(() => country().countryCode === CountryCode.IE)
  const countryIsUk = computed(() => country().countryCode === CountryCode.GB)
  const isDirectDebitFormMarket = computed(() => countryIsUk.value || countryIsNz.value)
  const countryIsUs = computed(() => country().countryCode === CountryCode.US)
  const countryIsCa = computed(() => country().countryCode === CountryCode.CA)
  const countryIsBe = computed(() => country().countryCode === CountryCode.BE)
  const countryIsDe = computed(() => country().countryCode === CountryCode.DE)
  const countryIsFr = computed(() => country().countryCode === CountryCode.FR)
  const countryIsNl = computed(() => country().countryCode === CountryCode.NL)
  const countryIsQc = computed(() => country().culture === Culture.FR_CA)
  const isEuCountry = computed(() => [CountryCode.AT, CountryCode.BE, CountryCode.DE, CountryCode.FR, CountryCode.GB, CountryCode.IE, CountryCode.NL].includes(country().countryCode))
  const isNaCountry = computed(() => [CountryCode.CA, CountryCode.US].includes(country().countryCode))
  const isSpCountry = computed(() => [CountryCode.NZ, CountryCode.AU].includes(country().countryCode))
  const eftConfig = computed(() => importedEftRegions.find(r => r.countries.includes(country().countryCode))!)
  const currency = computed(() => country().currency)
  const currencyForCountry = (countryCode?: string) => {
    if (!countryCode) { return currency.value }
    return country(countryCode).currency || currency.value
  }
  const currencySign = computed(() => {
    if (country().currency === Currency.EUR) {
      return '€'
    } else if (country().currency === Currency.GBP) {
      return '£'
    } else {
      return '$'
    }
  })
  const states = computed(() => country().states ?? [])
  const billingCountries = computed(() => billingRegions.value?.map(region => region.countries).flat())

  // Keyed lookup into a region/country's shared config fields. Overloaded so each
  // RegionAndCountryFields key returns its real value type (e.g. phoneNumberFormat
  // -> string, addressConfig -> AddressFormConfig) without callers casting.
  function localeData<K extends RegionAndCountryFields>(key: K, countryCode?: string): Region[K] | null
  function localeData (key: RegionAndCountryFields, countryCode: string | undefined, merge: true): Record<string, unknown>
  function localeData (key: RegionAndCountryFields, countryCode?: string, merge?: boolean): unknown {
    const regionData = region(countryCode)
    const countryData = country(countryCode)
    if (merge) {
      return { ...((regionData[key] || {}) as Record<string, unknown>), ...((countryData[key] || {}) as Record<string, unknown>) }
    }
    return countryData[key] || regionData[key] || null
  }
  const isVatCountry = computed(() => isSpCountry.value || Boolean(localeData(RegionAndCountryFields.vatRules)))

  const getStatesForCountry = async (countryCode: string) => {
    const cultureForCountryCode = country(countryCode).culture
    const targetCountry = country(countryCode)
    const shouldHaveStates = [CountryCode.US, CountryCode.CA, CountryCode.AU].includes(targetCountry.countryCode as CountryCode)
    if (!shouldHaveStates) { return [] }

    const countriesData = await $api()<ArrayOfTOneOrMore<CountryAPIData>>('address/countries', { culture: cultureForCountryCode })
    const { stateFieldName, states } = countriesData[0]
    fetchedStates.value[countryCode as CountryCode] = { stateFieldName, states }
    return { stateFieldName, states }
  }

  const getBaseUrl = (culture: Culture) => {
    const domainPrefix = getStampinupDomainEnvPrefix($config.public.ENVIRONMENT ?? Env.Release)
    const domain = getStampinupDomain(culture)
    const port = $config.public.ENVIRONMENT === Env.Local && $config.public.HTTP_PORT ? `:${$config.public.HTTP_PORT}` : ''
    const lang = getLanguagePath(culture)
    const baseUrl = `${Protocol.HTTPS}${domainPrefix}${domain}${port}${lang}`
    return baseUrl
  }
  const updateBaseUrl = (culture: Culture) => {
    baseUrl.value = getBaseUrl(culture)
  }

  const getDemoLocaleUrl = (contact: DemoContact) => {
    const localeMap = {
      [CountryCode.BE]: {
        [Language.FR]: Culture.FR_BE,
        [Language.DE]: Culture.DE_BE,
        [Language.NL]: Culture.NL_BE,
        [Language.NA]: Culture.NL_BE,
        [Language.EN]: Culture.NL_BE
      },
      [CountryCode.CA]: {
        [Language.FR]: Culture.FR_CA,
        [Language.EN]: Culture.EN_CA,
        [Language.NA]: Culture.EN_CA,
        [Language.DE]: Culture.EN_CA,
        [Language.NL]: Culture.EN_CA
      }
    }
    const primaryLanguage = stringToEnumValue(Language, contact.primaryLanguage)
    if (countryIsMultipleLanguages.value && primaryLanguage) {
      const locale = localeMap[country().countryCode as keyof typeof localeMap][primaryLanguage ?? Language.NA]
      if (country().culture !== locale) {
        return getBaseUrl(locale)
      }
    }
  }

  const idaPersonalInfo = (countryCode?: string) => {
    const countryPersonalFields = country(countryCode)[RegionAndCountryFields.idaPersonalInfo] ?? []
    const regionPersonalFields = region(countryCode)[RegionAndCountryFields.idaPersonalInfo] ?? []

    const fields: PersonalInfoField[] = [...regionPersonalFields]
    countryPersonalFields.forEach((field: PersonalInfoField) => {
      const existingFieldIndex = fields.findIndex(el => el.name === field.name)
      if (existingFieldIndex !== -1) {
        if (field.type === 'remove') {
          fields.splice(existingFieldIndex, 1)
        } else {
          fields[existingFieldIndex] = field
        }
      } else {
        fields.push(field)
      }
    })
    return fields
  }

  const billingAddressConfig = (countryCode: string) => {
    const region = billingRegions.value.find(r =>
      r.countries.find(country => country.countryCode === countryCode))
    const country = billingCountries.value.find(country => country.countryCode === countryCode)

    const countryAddressFields = country?.billingAddressConfig?.fields ?? []
    const regionAddressFields = region?.billingAddressConfig?.fields ?? []

    const fields: AddressFormConfig['fields'] = [...regionAddressFields]
    countryAddressFields.forEach((field) => {
      const existingFieldIndex = fields.findIndex(el => el.name === field.name)
      if (existingFieldIndex !== -1) {
        if (field.type === 'remove') {
          fields.splice(existingFieldIndex, 1)
        } else {
          fields[existingFieldIndex] = field
        }
      } else {
        fields.push(field)
      }
    })
    return {
      validate: country?.billingAddressConfig?.validate ?? region?.billingAddressConfig?.validate,
      display: country?.billingAddressConfig?.display ?? region?.billingAddressConfig?.display,
      fields
    } as AddressFormConfig
  }

  const addressConfig = (countryCode?: string): AddressFormConfig => {
    const countryAddressConfig = country(countryCode)[RegionAndCountryFields.addressConfig]
    const regionAddressConfig = region(countryCode)[RegionAndCountryFields.addressConfig]

    const countryAddressFields = countryAddressConfig?.fields ?? []
    const regionAddressFields = regionAddressConfig?.fields ?? []

    const fields: AddressFormConfig['fields'] = [...regionAddressFields]
    countryAddressFields.forEach((field) => {
      const existingFieldIndex = fields.findIndex(el => el.name === field.name)
      if (existingFieldIndex !== -1) {
        if (field.type === 'remove') {
          fields.splice(existingFieldIndex, 1)
        } else {
          fields[existingFieldIndex] = field
        }
      } else {
        fields.push(field)
      }
    })
    const result: AddressFormConfig = {
      validate: countryAddressConfig?.validate ?? regionAddressConfig?.validate,
      display: countryAddressConfig?.display ?? regionAddressConfig?.display,
      fields
    }
    return result
  }
  const stateFieldName = computed(() => country().stateFieldName ?? '')
  const countryIsMultipleLanguages = computed(() => {
    const multiLangCountries = countries.value.filter(countryLocal => countries.value.filter(counryDuplicate => counryDuplicate.name === countryLocal.name).length > 1)
    return !!multiLangCountries.find(c => c.countryCode === country().countryCode)
  }
  )
  const getBillingRegions = () => {
    if (billingRegions.value.length !== 0) { return }

    // Load all billing regions to support cross-border billing addresses
    // This allows US cards on EU domains, etc.
    billingRegions.value = importedBillingRegions
  }
  const countryPreferredLanguages = computed(() => {
    if (countryIsMultipleLanguages.value) {
      return country().countryCode === CountryCode.BE ? ['DE', 'FR', 'NL'] : ['EN', 'FR']
    }
    return [country().culture.split('-')[0]!.toUpperCase()]
  })
  const formatCurrency = (value: number) => {
    const formatter = new Intl.NumberFormat(culture.value, {
      style: 'currency',
      currency: currency.value,
      minimumFractionDigits: 2
    })
    return formatter.format(value)
  }

  const changeAuthenticatedLocale = async (switchLocalePayload: SwitchLocalePayload) => {
    const cultureNew = switchLocalePayload?.culture

    // get out if no culture
    if (!cultureNew || cultureNew === culture.value) { return }
    const rootStore = useRootStore()
    rootStore.rootLoading = true
    // The overlay stays up only while the redirect takes the visitor away. Any
    // other way out, a throw included, leaves them here with the page hidden.
    let leavingPage = false

    try {
      const response = await $api()<{bearerToken: string}>(`account/switch-culture/${cultureNew}`, { method: 'POST' })
      const { bearerToken } = response
      if (!bearerToken) {
        $appInsights.trackException({ exception: new Error(`switch locale didnt return bearerToken. response ${JSON.stringify(response)}`) })
        return
      }

      const lang = getLanguagePath(cultureNew)
      const cartStore = useCartStore()

      // Same rule as changeLocale below: the cart's demonstrator first, then the
      // request's referral in whatever casing it came, carried on as `demoid`.
      const demoId = cartStore.demonstrator?.demoId || findAttributionParam(route.query)?.value

      const persistentStore = usePersistentStore()
      const query = {
        ...Object.fromEntries(Object.entries(route.query).filter(([key]) => !isAttributionKey(key))),
        demoid: demoId ? String(demoId) : undefined, // undefined is removed, null is not,
        authToken: bearerToken,
        culture: cultureNew,
        lang: lang || undefined,
        viatoken: demoId ? 'true' : undefined,
        sharedCartId: persistentStore.getPersistentValue('sharedCartId') || undefined
      }

      try {
        // Set to anonymous session essentially logging out on old domain to avoid cart duplication
        await rootStore.fetchAnonymousSession()

        // set up new domain (logout has to be done before this otherwise it will be done on the new domain)
        updateBaseUrl(culture.value)

        // redirect to new domain
        const { redirect } = useRedirect()
        await redirect(`${getBaseUrl(cultureNew)}${stringifyQuery(query)}`)
        leavingPage = true
      } catch (ex) {
        $appInsights.trackTrace({ message: toError(ex).message })
      }
    } finally {
      if (!leavingPage) { rootStore.rootLoading = false }
    }
  }
  const changeLocale = async (payload: {
    demoCulture: Culture
    routeToKeep: string
    route?: RouteLocationNormalizedLoaded
    attribution?: AttributionSelection
  }) => {
    // get out if no culture
    if (!payload.demoCulture) { return }

    const rootStore = useRootStore()
    rootStore.rootLoading = true
    if (culture.value !== payload.demoCulture) {
      updateBaseUrl(payload.demoCulture)
    }

    const routeActual = payload.route || route
    const cartStore = useCartStore()
    // An explicit referral choice must survive the handoff, even if the cart
    // still carries a different demonstrator. Manual switches keep cart priority.
    const demoId = payload.attribution?.value
      ?? (cartStore.demonstrator?.demoId || findAttributionParam(routeActual.query)?.value)
    const editableQuery = Object.fromEntries(
      Object.entries(routeActual.query)
        .filter(([key]) => !isAttributionKey(key))
        .map(([k, v]) => [k, v == null ? undefined : String(v)])
    ) as Record<string, string | undefined>
    const query: Record<string, string | undefined> = {
      ...editableQuery,
      demoid: demoId != null ? String(demoId) : undefined // undefined is removed, null is not,
    }

    const queryString = stringifyQuery(query)
    const routeToKeep = culture.value === payload.demoCulture ? route.path.substring(1) : payload.routeToKeep
    const url = routeToKeep ? `${baseUrl.value}/${routeToKeep}${queryString}` : `${baseUrl.value}/${queryString}`
    try {
      const { redirect } = useRedirect()
      // Awaited so an SSR handoff has its redirect response in place before the
      // calling plugin returns.
      await redirect(url)
    } catch (ex) {
      // The visitor stays on this page, so take the overlay back off it. First,
      // so a failure to report cannot leave it up.
      rootStore.rootLoading = false
      // No client until App Insights is configured (the server plugin provides
      // applicationinsights' defaultClient as it finds it).
      $appInsights?.trackTrace({ message: toError(ex).message })
    }
  }
  // Returns the stampinup domain the host matched (null when none did), so the
  // caller acts on this decision instead of re-deriving it from the same header.
  const getLocaleFromHeaders = (hostHeader?: string, lang?: string): StampinupDomain | null => {
    if (!hostHeader) {
      return null
    }

    const stampinupDomain = getStampinupDomainFromHost(hostHeader)

    if (stampinupDomain) {
      const locale = getLocaleFromStampinupDomain(stampinupDomain)
      if (!locale) {
        return stampinupDomain
      }
      const languages = languagesForDomain(stampinupDomain)

      // Set locale if lang segment
      if (lang) {
        if (languages.includes(lang)) {
          locale.culture = `${lang}-${locale.countryCode.toLowerCase()}` as Culture
          subLocaleLang.value = lang
        } else {
          throw createError({
            statusCode: 404
          })
        }
      }

      culture.value = locale.culture
      isLocaleSet.value = true
      localeSource.value = LocaleSource.HTTPHostHeader
    }
    return stampinupDomain
  }

  const fallbackLocale = (defaultCulture: Culture) => {
    culture.value = defaultCulture
    isLocaleSet.value = true
    localeSource.value = LocaleSource.DefaultOrFailBack
  }

  return {
    culture,
    currency,
    currencyForCountry,
    baseUrl,
    currencySign,
    fetchedStates,
    countries,
    states,
    stateFieldName,
    billingRegions,
    env,
    isLocaleSet,
    subLocaleLang,
    eftConfig,
    countryPreferredLanguages,
    getStatesForCountry,
    getBaseUrl,
    idaPersonalInfo,
    billingAddressConfig,
    addressConfig,
    getBillingRegions,
    formatCurrency,
    changeLocale,
    changeAuthenticatedLocale,
    billingCountries,
    localeData,
    country,
    region,
    sortedRegions,
    countryIsAt,
    countryIsAu,
    countryIsNl,
    countryIsNz,
    countryIsMultipleLanguages,
    countryIsIe,
    countryIsUk,
    countryIsUs,
    countryIsBe,
    countryIsFr,
    countryIsCa,
    countryIsQc,
    countryIsDe,
    isEuCountry,
    isNaCountry,
    isSpCountry,
    isDirectDebitFormMarket,
    isVatCountry,
    getLocaleFromHeaders,
    fallbackLocale,
    updateBaseUrl,
    getDemoLocaleUrl
  }
})
